/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { FileText, Image as ImageIcon, Camera, X } from "lucide-react";
import { SpinnerCircularFixed } from "spinners-react";
import { ErrorModalContext } from "../AlertsModal/AlertsModal";
import { saveFile, signFile } from "../../services/files";
import { getValidFileName } from "../../utils/strings";

interface FileUploaderProps {
  fileName: string;
  acceptedTypes: string[];
  maxSize?: number;
  onChange: (url: string | File) => void; // Can return URL or File object
  placeholder?: string;
  disabled?: boolean;
  isFullWidth?: boolean;
  className?: string;
  currentFileUrl?: string;
  icon?: React.ReactNode; // Custom icon prop
  mode?: "upload" | "select"; // "upload" = immediate S3 upload, "select" = store File object
  label?: string; // Label for the uploader
  customText?: string; // Custom text to display below the placeholder
  showCamera?: boolean; // Whether to show camera button
  onCameraClick?: () => void; // Camera click handler (optional, will use internal if not provided)
  cameraTitle?: string; // Custom title for the camera modal
  useFileName?: boolean; // Use the file name as the key in the S3 bucket
}

const FileUploader = ({
  fileName,
  acceptedTypes,
  maxSize = 10485760, // 10MB default
  onChange,
  placeholder = "Haz click o arrastra hasta aquí un archivo",
  disabled = false,
  isFullWidth = false,
  className,
  currentFileUrl,
  icon,
  mode = "upload", // Default to immediate upload
  label,
  customText,
  showCamera,
  onCameraClick,
  cameraTitle,
  useFileName = false,
}: FileUploaderProps) => {
  const [loading, setLoading] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { showAlert } = useContext(ErrorModalContext);

  // Add drag and drop functionality
  useEffect(() => {
    window.addEventListener("dragover", onDrag);
    return () => {
      window.removeEventListener("dragover", onDrag);
    };
  }, []);

  const onDrag = (e: any) => e.preventDefault();

  // Camera functionality
  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      fileInputClick();
      return;
    }
    try {
      setShowCameraModal(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      // Try front camera as fallback
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user', // Front camera
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          } 
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (fallbackError) {
        console.error('Error accessing front camera:', fallbackError);
        showAlert('No se pudo acceder a la cámara', 'error');
        setShowCameraModal(false);
      }
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (context) {
        // Set canvas size to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw the video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a proper File object from the blob
            const file = new (window as any).File([blob], `photo_${Date.now()}.jpg`, {
              type: 'image/jpeg'
            });
            
            // Handle the file based on mode
            if (mode === "select") {
              onChange(file);
            } else {
              // Upload to S3 immediately
              uploadFile(file);
            }
            
            stopCamera();
          }
        }, 'image/jpeg', 0.8);
      }
    }
  };

  const handleCameraClick = () => {
    if (onCameraClick) {
      onCameraClick();
    } else {
      startCamera();
    }
   };

  // Validate file type
  const isValidFileType = (file: File): boolean => {
    return acceptedTypes.some(type => {
      if (type.includes("*")) {
        // Handle wildcards like "image/*"
        const baseType = type.split("/")[0];
        return file.type.startsWith(baseType + "/");
      }
      return file.type === type;
    });
  };



  // Handle file selection
  const handleFile = async (file: File) => {
    // Validate file type
    if (!isValidFileType(file)) {
      showAlert(
        `Tipo de archivo no válido. Tipos permitidos: ${acceptedTypes.join(", ")}`,
        "error"
      );
      return;
    }

      // Validate file size
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    showAlert(`El tamaño máximo permitido es de ${maxSizeMB} MB`, "error");
    return;
  }

    // Handle based on mode
    if (mode === "select") {
      // Just return the File object
      onChange(file);
      // Reset input value so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      // Upload to S3 immediately
      await uploadFile(file);
    }
  };

  // Handle file selection from file explorer
  const onFileChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  // Handle drag and drop
  const onFileDrop = (e: any) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  // Upload file to S3
  const uploadFile = async (file: File) => {
    try {
      setLoading(true);

      const uploadURL = await signFile(
        fileName,
        file.type,
        useFileName ? getValidFileName(file.name) : "",
      );
      await saveFile(uploadURL, file);
      
      const cleanUrl = uploadURL.split("?").shift() || "";
      onChange(cleanUrl);
    } catch (e) {
      showAlert("Ocurrió un error inesperado", "error");
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Open file explorer
  const fileInputClick = () => {
    if (disabled) return;
    if (fileInputRef.current) return fileInputRef.current.click();
  };

  // Get file type display name
  const getFileTypeDisplay = () => {
    const types = acceptedTypes.map(type => {
      if (type === "image/*") return "imágenes";
      if (type === "application/pdf") return "PDFs";
      if (type.startsWith("image/")) return "imágenes";
      return type;
    });
    return types.join(", ");
  };

  // Get icon based on accepted types or custom icon
  const getIcon = () => {
    if (icon) {
      return icon;
    }
    if (acceptedTypes.some(type => type.includes("image"))) {
      return <ImageIcon className="h-6 w-6 text-gray-400" />;
    }
    return <FileText className="h-6 w-6 text-gray-400" />;
  };

  // Extract filename from URL or return the original string
  const getDisplayName = (fileUrl: string): string => {
    if (!fileUrl) return "";
    
    // If it's already a filename (no http/https), return as is
    if (!fileUrl.startsWith('http')) {
      return fileUrl;
    }
    
    // For URLs
    return "Subir nuevo archivo";
  };

  return (
    <div className="flex flex-col">
      {label && (
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-normal text-gray-500">
            {label}
          </label>
          {showCamera && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCameraClick();
              }}
              className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
              title="Tomar foto con cámara"
            >
              <Camera className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <div className="relative">
        <div
          className={clsx(
            "flex flex-col rounded-lg border border-dashed border-gray-300 bg-white",
            "relative items-center justify-center object-cover",
            isFullWidth ? "w-full" : "w-[258px]",
            !disabled && "cursor-pointer",
            "h-[200px] min-w-[200px]",
            className
          )}
          onClick={fileInputClick}
          onDrop={onFileDrop}
        >
        {loading && (
          <SpinnerCircularFixed
            size={20}
            thickness={50}
            speed={250}
            color="rgba(215, 1, 90, 1)"
            secondaryColor="rgba(241, 241, 241, 1)"
          />
        )}
        
        {!loading && currentFileUrl ? (
          <div className="h-full w-full rounded-lg flex items-center justify-center">
            <div className="text-center flex flex-col items-center">
              {getIcon()}
              <p className="text-sm text-gray-600 mt-2">{getDisplayName(currentFileUrl)}</p>
              <p className="text-xs text-gray-400 mt-1">Archivo seleccionado</p>
            </div>
          </div>
        ) : (
          !loading && (
            <div
              className={`flex h-full w-full flex-col items-center justify-center p-[10px] text-center text-sm text-gray-500 ${
                disabled ? "bg-gray-50" : "bg-white"
              }`}
            >
              <div className="rounded-full bg-gray-50 p-3">
                {getIcon()}
              </div>
              <label
                className={`mt-2 text-center text-sm text-gray-500 ${
                  disabled ? "" : "cursor-pointer"
                }`}
              >
                {placeholder}
              </label>
              <label className="mt-1 text-center text-xs text-gray-400">
                {customText || `Tipos: ${getFileTypeDisplay()}`}
              </label>
            </div>
          )
        )}
        
        <input
          className="hidden"
          type="file"
          accept={acceptedTypes.join(",")}
          ref={fileInputRef}
          onChange={onFileChange}
        />
        </div>
      </div>

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="relative max-w-md w-full mx-4 bg-white rounded-lg overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {cameraTitle || "Tomar foto"}
                </h3>
                <button onClick={stopCamera} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="relative">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-64 object-cover" />
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <div className="p-4 flex justify-center">
              <button onClick={capturePhoto} className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
                Capturar Foto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader; 